import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analytics?days=${days}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <p className="text-center py-12 text-gray-400 text-sm">Loading...</p>;
  if (!data) return <p className="text-center py-12 text-gray-400 text-sm">Failed to load</p>;

  const maxViews = Math.max(...(data.daily.map(d => d.views) || [1]), 1);

  return (
    <div data-testid="analytics-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl font-normal text-gray-900">Analytics</h2>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="analytics-period-selector">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${days === d ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[
          { label: 'Page Views', value: data.views, color: 'bg-blue-50 text-blue-700' },
          { label: 'Link Clicks', value: data.link_clicks, color: 'bg-violet-50 text-violet-700' },
          { label: 'Contacts', value: data.total_contacts, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Conversion', value: `${data.conversion_rate}%`, color: 'bg-amber-50 text-amber-700' },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-xl p-5" data-testid={`analytics-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{s.label}</p>
            <p className="text-3xl font-heading font-normal text-gray-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {data.daily.length > 0 && (
        <div className="mb-10">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Daily Activity</h3>
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-end gap-[3px] h-32" data-testid="analytics-chart">
              {data.daily.slice(-14).map(d => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-8 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    {d.date.slice(5)}: {d.views}v \u00b7 {d.clicks}c \u00b7 {d.contacts}s
                  </div>
                  <div className="w-full flex flex-col gap-[1px]">
                    <div className="w-full bg-blue-400 rounded-t" style={{ height: `${Math.max((d.views / maxViews) * 100, 4)}px` }} />
                    {d.clicks > 0 && <div className="w-full bg-violet-400 rounded" style={{ height: `${Math.max((d.clicks / maxViews) * 80, 3)}px` }} />}
                  </div>
                  <span className="text-[9px] text-gray-400 font-medium">{d.date.slice(8)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
              <span className="flex items-center gap-1.5 text-[10px] text-gray-400"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400"></span>Views</span>
              <span className="flex items-center gap-1.5 text-[10px] text-gray-400"><span className="w-2.5 h-2.5 rounded-sm bg-violet-400"></span>Clicks</span>
            </div>
          </div>
        </div>
      )}

      {data.top_links && data.top_links.length > 0 && (
        <div className="mb-10">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Top Links</h3>
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid="analytics-top-links">
            {data.top_links.map((link, i) => (
              <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{link.name || 'Unnamed'}</p>
                  <p className="text-[11px] text-gray-400 truncate">{link.url}</p>
                </div>
                <span className="text-sm font-semibold text-gray-900 ml-4 shrink-0">{link.clicks}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.recent_contacts && data.recent_contacts.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Recent Contacts</h3>
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid="analytics-recent-contacts">
            {data.recent_contacts.map((c, i) => (
              <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{c.visitor_name}</p>
                  <p className="text-[11px] text-gray-400">{c.visitor_company || c.visitor_email || c.visitor_phone}</p>
                </div>
                <span className="text-[11px] text-gray-400 shrink-0 ml-4">{c.created_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
